import {
  ApolloCache,
  RefetchQueriesInclude,
  useMutation,
} from "@apollo/client";
import { gql } from "generated-graphql";
import {
  DocumentAssociationsInput,
  DocumentInput2,
  Document,
} from "generated-graphql/graphql";
import { useCallback, useMemo } from "react";

export const UPSERT_DOCUMENTS_MUTATION = gql(`
  mutation UpsertDocuments(
    $documents: [DocumentInput2!]!
    $associations: [DocumentAssociationsInput!]
    $removeAssociations: [DocumentAssociationsInput!]
    $currentSubmissionDocument: Boolean!
    $generateTitleOnCreate: Boolean
  ) {
    upsertDocuments(
      documents: $documents
      associations: $associations
      removeAssociations: $removeAssociations
      currentSubmissionDocument: $currentSubmissionDocument
      generateTitleOnCreate: $generateTitleOnCreate
    ) {
      id
      title
      documentType
      storageLink
      fileExtension
    }
  }
`);

export function useUpsertDocuments(config?: {
  update?: (cache: ApolloCache<any>, { data }: { data: any }) => void;
  generateTitleOnCreate?: boolean;
  refetchQueries?: RefetchQueriesInclude;
}) {
  const [upsertDocumentsMutation, { data, loading, error }] = useMutation(
    UPSERT_DOCUMENTS_MUTATION,
    {
      update(cache, { data }) {
        config?.update?.(cache, { data });
      },
      refetchQueries: config?.refetchQueries,
    }
  );

  const upsertDocuments = useCallback(
    async ({
      documents,
      associations,
      removeAssociations,
      currentSubmissionDocument,
      generateTitleOnCreate,
    }: {
      documents: DocumentInput2[];
      currentSubmissionDocument: boolean;
      associations?: DocumentAssociationsInput[];
      removeAssociations?: DocumentAssociationsInput[];
      generateTitleOnCreate?: boolean;
    }) => {
      // Validate all documents have tenantId
      for (const doc of documents) {
        if (!doc.tenantId) {
          throw new Error("Tenant ID is required for all documents");
        }
      }

      const result = await upsertDocumentsMutation({
        variables: {
          documents,
          associations: associations ?? [],
          removeAssociations: removeAssociations ?? [],
          currentSubmissionDocument,
          generateTitleOnCreate,
        },
      });

      return result.data?.upsertDocuments;
    },
    [upsertDocumentsMutation]
  );

  return useMemo(
    () => ({
      data,
      upsertDocuments,
      loading,
      error,
      upsertDocumentsMutation,
    }),
    [data, upsertDocuments, loading, error, upsertDocumentsMutation]
  );
}
